<template>
  <div class="application">
    <div class="card-header bg-default">
      <span class="fa fa-user-plus mr-1"></span>
      Applicant Details
      <span class="badge"
        :class="{
          'badge-success': application.cardId,
          'badge-danger': !application.cardId
        }">
        <span class="fa fa-id-card"></span>
        {{ application.cardId ?
          application.Smartcard.cardNumber :
          'No Smartcard Assigned' }}
      </span>
      <span class="badge badge-danger"
        v-if="application.applicationFlagged">
        <span class="fa fa-flag"></span> Flagged
      </span>
      <span class="badge badge-danger"
        v-if="application.declined">
        <span class="fa fa-ban"></span> Declined
      </span>
      <span class="badge badge-success"
        v-if="application.applicationReviewed">
        <span class="fa fa-check"></span> Checked
      </span>
    </div>
    <div class="list-group list-group-flush">
      <div class="list-group-item">
        <span class="fa fa-user mr-1"></span>
        <router-link :to="`/membership/applications/${application.uuid}`">
          {{ application.dropdownLabel }} -
          <strong>{{ application.AccountType.name }}</strong>
        </router-link>
      </div>
      <div class="list-group-item list-group-item-danger"
        v-if="!application.isMasterAccount && application.Master">
        Member is signing up as a Driver under
        <router-link :to="`/membership/members/${application.Master.uuid}`">
          {{ application.Master.fullName }}
        </router-link>'s account.
      </div>
      <div class="list-group-item">
        <span class="fa fa-map-marker-alt mr-1"></span>
        {{ application.address }} {{ application.suburb }}
        {{ application.state }} {{ application.postcode }}
      </div>
      <div class="list-group-item">
        <span class="fa fa-birthday-cake mr-1"></span>
        {{ application.dateOfBirth | short_date_format }} ({{ age }} years old)
      </div>
      <div class="list-group-item list-group-item-danger" v-if="age < 21">
        <p><strong>Applicant is under 21</strong></p>
        <template v-if="application.AmazonImages.length === 0">
          <p>
            <button @click="sendGuarantor()" class="btn btn-info btn-xs">
              <span class="fa fa-envelope"></span> Email guarantor form
            </button>
            <button @click="uploadGuarantor()" class="btn btn-success btn-xs">
              <span class="fa fa-upload"></span> Upload guarantor form
            </button>
          </p>
        </template>
        <p v-else>
          <span class="fa fa-check-circle"></span>
          Guarantor form has been uploaded.
        </p>
      </div>
      <div class="list-group-item" :class="{
        'list-group-item-danger': !application.CreditCards.length,
        'list-group-item-success': application.CreditCards.length
      }">
        <span class="fa fa-credit-card mr-1"></span>
        {{ !application.CreditCards.length ? 'Applicant did not add a credit card.' : 'Applicant has added a credit card' }}
      </div>
      <div v-if="application.MasterUuid" class="list-group-item" :class="{
        'list-group-item-danger': !application.Master.CreditCards.length,
        'list-group-item-success': application.Master.CreditCards.length
      }">
        <span class="fa fa-credit-card"></span>
        {{ !application.Master.CreditCards.length ? 'Master account does not have a credit card on file.' : 'Master account has credit card on file.' }}
      </div>
      <div class="list-group-item list-group-item-info" v-if="application.PromoCodes.length > 0">
        <span class="fa fa-dollar-sign"></span>
        <strong>Promo Code Applied:</strong>
        {{ application.PromoCodes[0].code }} -
        {{ application.PromoCodes[0].description }}
      </div>
      <div class="list-group-item list-group-item-danger" v-if="application.drivingInfringements > 0">
        <strong>The amount of driving infringements applicant has
          had in the past 3 years:</strong>
          {{ application.drivingInfringements }}
      </div>
      <div class="list-group-item list-group-item-danger" v-if="application.accidents > 0">
        <strong>The amount of at fault accidents applicant has had
          in the past 3 years:</strong>
          {{ application.accidents }}
      </div>
      <div v-if="application.alcoholRelated" class="list-group-item list-group-item-danger">
        <strong>Has the applicant had any alcohol related
        violations in the past 7 years:</strong>
        {{ application.alcoholRelated ? "Yes" : "No" }}
      </div>
    </div>
    <div class="card-body">
      <div class="row">
        <div class="col-md-6">
          <h4 class="card-title">Drivers License</h4>
          <p>
            <strong>Licence Number: </strong>
            {{ application.driversLicense }}
          </p>
          <p>
            <strong>Licence Issued: </strong>
            {{ application.driversLicenseIssued }}
          </p>
          <p v-if="application.driversLicenseCountry">
            <strong>Other Country: </strong>
            {{ application.driversLicenseCountry }}
          </p>
          <p>
            <strong>Expiry: </strong>
            {{ application.driversLicenseExpiry | short_date_format }}
          </p>
          <p>
            <strong>Years Driving: </strong>
            {{ application.driversYears }}
          </p>
          <p>
            <a v-if="application.DriversLicense"
            :href="url + application.DriversLicense.objectKey"
            target="_blank">Download Front of License</a>
          </p>
          <p>
            <a v-if="application.DriversLicenseBack"
            :href="url + application.DriversLicenseBack.objectKey"
            target="_blank">Download Back of License</a>
          </p>
          <p>
            <a v-if="application.ProofOfAddress"
            :href="url + application.ProofOfAddress.objectKey"
            target="_blank">Download Proof of Address</a>
          </p>
          <template v-if="application.ProofOfEnrolment">
            <h4>Student Details</h4>
            <p>
              <a
              :href="url + application.ProofOfEnrolment.objectKey"
              target="_blank">Download Proof of Enrolment</a>
            </p>
            <p>
              <strong>Expiry: </strong>
              {{ application.studentExpiry | short_date_format }}
            </p>
          </template>
        </div>
        <div class="col-md-6">
          <h4 class="card-title">Latest Note</h4>
            <template v-if="application.AccountNotes[0]">
              <div class="list-group-item">
                <account-note-display :data="application.AccountNotes[0]">
                </account-note-display>
              </div>
            </template>
          <p class="list-group-item" v-else>
            No notes have been made for this application
          </p>
        </div>
      </div>
    </div>
    <div class="card-footer text-right">
      <a :href="`mailto:${application.email}`" class="btn btn-sm btn-amber">
        <span class="fa fa-envelope"></span> Email Applicant
      </a>
      <router-link class="btn btn-sm btn-primary" :to="`/membership/applications/${application.uuid}`">
        <span class="fa fa-search fa-fw"></span> View
      </router-link>
      <dropdown-input
        label="Actions"
        label-icon="fa-check-circle"
        :dropup="true"
        :is-small="true"
        :actions="applicationActions(application, loggedInUser)"
        @action="executeAction($event, application)">
      </dropdown-input>
    </div>
  </div>
</template>

<script>
import AccountNoteDisplay from '@/components/displays/AccountNoteDisplay'
import DropdownInput from '@/components/inputs/DropdownInput'
import { mapGetters, mapActions } from 'vuex'
import {
  ADD_SMARTCARD,
  EMAIL_APPLICATION,
  FILL_APPLICATION,
  CHECK_APPLICATION,
  MOVE_TO_APPLICATIONS,
  CHARGE_MEMBERSHIP,
  CHARGE_SETUP,
  CHARGE_DRIVER,
  DOWNLOAD_WELCOME_LETTER,
  UNASSIGN_SMARTCARD,
  EDIT_APPLICATION,
  EDIT_SMARTCARD,
  APPROVE_APPLICATION,
  BLACKLIST_MEMBER,
  FLAG_APPLICATION,
  DECLINE_APPLICATION,
  DELETE_APPLICATION,
  SEND_GUARANTOR_FORM,
  UPLOAD_GUARANTOR_FORM,
  ADD_ACCOUNT_NOTE,
  WAIVE_MEMBERSHIP,
  UNFLAG_APPLICATION
} from '@/vuex/types'
import moment from 'moment-timezone'

export default {
  name: 'Application',
  components: {
    AccountNoteDisplay,
    DropdownInput
  },
  props: {
    object: {
      type: Object
    }
  },
  computed: {
    age () {
      return moment().diff(moment(this.application.dateOfBirth), 'years')
    },
    application () {
      return {
        AccountType: {},
        DriversLicense: {},
        DriversLicenseBack: {},
        AccountNotes: [],
        PromoCodes: [],
        AmazonImages: [],
        ...this.object,
        ...this.updatedApplication
      }
    },
    ...mapGetters([
      'applicationActions',
      'loggedInUser'
    ])
  },
  data () {
    return {
      updatedApplication: {},
      url: 'https://techcar-dev.s3.amazonaws.com/'
    }
  },
  methods: {
    async executeAction (action, payload) {
      if (action === DOWNLOAD_WELCOME_LETTER) {
        return this.downloadWelcomeLetter()
      } else {
        let result = await this[action](payload)
        if (result && result.updated) this.updatedApplication = result.updated
      }
    },
    uploadGuarantor () {
      this.executeAction(UPLOAD_GUARANTOR_FORM, {
        application: this.application
      })
    },
    sendGuarantor () {
      this.executeAction(SEND_GUARANTOR_FORM, {
        application: this.application
      })
    },
    downloadWelcomeLetter () {
      window.open('/welcome-letter/' + this.application.uuid)
    },
    ...mapActions([
      ADD_SMARTCARD,
      EMAIL_APPLICATION,
      FILL_APPLICATION,
      CHECK_APPLICATION,
      MOVE_TO_APPLICATIONS,
      CHARGE_MEMBERSHIP,
      EDIT_APPLICATION,
      EDIT_SMARTCARD,
      CHARGE_SETUP,
      CHARGE_DRIVER,
      UNASSIGN_SMARTCARD,
      APPROVE_APPLICATION,
      FLAG_APPLICATION,
      DECLINE_APPLICATION,
      DELETE_APPLICATION,
      BLACKLIST_MEMBER,
      SEND_GUARANTOR_FORM,
      UPLOAD_GUARANTOR_FORM,
      ADD_ACCOUNT_NOTE,
      WAIVE_MEMBERSHIP,
      UNFLAG_APPLICATION
    ])
  }
}
</script>



// WEBPACK FOOTER //
// src/components/cards/Application.vue